import {
  NETWORKS_QUERY_KEY,
  useGetOneNetwork,
} from "#hooks/adapters/useNetworks";
import { useNavigate, useParams, useSearchParams } from "#routers/hooks";
import { NetworkListRoute } from "#routes/organization/networks";
import { NetworkCalculationTab } from "#routes/organization/networks/[networkId]/detail/NetworkCalculationTab";
import { NetworkResultsTab } from "#routes/organization/networks/[networkId]/detail/NetworkResultsTab/NetworkResultsTab";
import EditAssetGroupDialog from "#src/batteries-included-components/Dialogs/EditAssetGroupDialog";
import { UpdateEntityStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateEntityStatusDialog";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { PageErrorContent } from "#src/batteries-included-components/Layouts/Errors/PageError";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useUpdateAssetStatusAsync } from "#src/components/hooks/adapters/useAssets";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import { useCreateEditMetaSegments } from "#src/components/hooks/useCreateEditMetaSegments";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { AssociatedFormSubmissionsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedFormSubmissionsTab";
import { getAssetFilters } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflows.helpers";
import { AssociatedWorkflowsTab } from "#src/routes/organization/facilities/[facilityId]/AssociatedWorkflowsTab";
import { useBreadcrumbsFromRoute } from "#utils/route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DropdownMenu,
  generateDocumentTitle,
  Icon,
  Page,
  Pill,
  Skeleton,
  Tab,
  useToast,
} from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  FormSubmissionAdapter,
  isResponseErrorType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useState } from "react";
import { Titled } from "react-titled";
import { NetworkCalculationTabV2 } from "./NetworkCalculationTabV2";
import { NetworkDetailsTab } from "./NetworkDetailsTab/NetworkDetailsTab";
import { NetworkDetailRoute } from "./index";

const DEFAULT_TAB_KEY = "details";

export const NetworkDetailPage = () => {
  const { networkId } = useParams<{ networkId: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdateNetworkStatusDialogOpen, setIsUpdateNetworkStatusDialogOpen] =
    useState(false);
  const [areFormsEnabled, isLoadingAreFormsEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:forms",
    permissionQuery: {
      $and: [
        "form_categories:read",
        "form_schemas:read",
        "form_submissions:read",
      ],
    },
  });
  const [areWorkflowsEnabled, isLoadingAreWorkflowsEnabled] =
    useIsFeatureAvailable({
      featureFlagQuery: "core:workflows",
      permissionQuery: {
        $and: ["workflows:read", "workflow_templates:read"],
      },
    });
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [filters] = useStickyState<{ period?: { from?: string } }>(
    {},
    assetsSharedViewConfigStorageKey
  );

  const {
    data: assetGroup,
    isLoading,
    error: assetGroupError,
  } = useGetOneNetwork(networkId, {
    period: getYearMonthFromDateRange(filters.period),
  });
  const createEditMetaSegments = useCreateEditMetaSegments({
    editedAt: assetGroup?.updated_at,
    createdAt: assetGroup?.created_at,
  });

  const [breadcrumbs] = useBreadcrumbsFromRoute(NetworkDetailRoute, {
    detail: { title: assetGroup?.name },
  });

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof AssetGroupAdapter.deleteOne>[0]) => {
      return AssetGroupAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.ASSET_GROUP)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [Resources.ASSET_GROUP] });
      navigate(NetworkListRoute.toLinkParts());
    },
    onError: (error) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.ASSET_GROUP)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.ASSET_GROUP)}. An unknown error occurred.`,
        });
      }
    },
  });
  const relatedFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, Resources.ASSET_GROUP, networkId],
    queryFn: () => {
      return FormSubmissionAdapter.getList({
        filters: {
          related_entity_id: networkId,
        },
      });
    },
    enabled: areFormsEnabled && !!networkId,
  });
  const relatedWorkflowsQuery = useListWorkflows(
    {
      page: 1,
      pageSize: 1,
      filters: {
        ...getAssetFilters(Resources.ASSET_GROUP, { assetId: networkId }),
      },
    },
    {
      enabled: areWorkflowsEnabled && !!networkId,
    }
  );

  const [updateNetworkStatusJobStorageKey] = useStorageKey(
    ["async-job"],
    `update-network-status-${networkId}`
  );

  const updateStatusMutation = useUpdateAssetStatusAsync({
    jobKey: updateNetworkStatusJobStorageKey,
    assetId: networkId,
    setIsUpdating: setIsUpdatingStatus,
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [
          NETWORKS_QUERY_KEY,
          "getOne",
          networkId,
          { period: getYearMonthFromDateRange(filters.period) },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.ACTIVITY],
      });
    },
  });

  const onActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ ...searchParams, tab: tabKey });
  };

  const onClickEditNetwork = () => {
    setAssetIdToEdit(networkId);
  };
  const handleChangeNetworkStatusClick = () =>
    setIsUpdateNetworkStatusDialogOpen(true);

  const hasAssociatedResources =
    !!relatedWorkflowsQuery.data?.total_entries ||
    !!relatedFormSubmissionsQuery.data?.total_entries;

  return (
    <>
      <Page
        title={
          assetGroup ? (
            <>
              {assetGroup?.name}{" "}
              <Pill
                variant={
                  assetGroup?.status === "active" ? "success" : "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(assetGroup?.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            NetworkDetailRoute.title
          )
        }
        category={localize(NetworkDetailRoute.previous?.title ?? "")}
        breadcrumbs={breadcrumbs}
        renderMeta={
          createEditMetaSegments.length === 0
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={createEditMetaSegments} />
              )
        }
        activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
        onActiveTabKeyChange={onActiveTabKeyChange}
        isLoading={
          isLoadingLocalization ||
          isLoading ||
          isLoadingAreFormsEnabled ||
          isLoadingAreWorkflowsEnabled
        }
        actionRow={[
          <IsFeatureAvailable
            key="edit-access-policy"
            featureFlagQuery="core:networks"
            permissionQuery="networks:write"
            fallbackChildren={null}
          >
            <Button
              onClick={onClickEditNetwork}
              key="edit"
            >
              Edit
            </Button>
          </IsFeatureAvailable>,
          <IsFeatureAvailable
            key="change-status-access-policy"
            featureFlagQuery="core:networks"
            permissionQuery="networks:write"
            fallbackChildren={null}
          >
            <Button
              onClick={handleChangeNetworkStatusClick}
              key="change-status"
              isLoading={isUpdatingStatus}
            >
              Change Status
            </Button>
          </IsFeatureAvailable>,
          <IsFeatureAvailable
            key="more-access-policy"
            featureFlagQuery="core:networks"
            permissionQuery="networks:delete"
            fallbackChildren={null}
          >
            <DropdownMenu
              key="more"
              options={[
                {
                  label: "Delete",
                  variant: "danger",
                  isDisabled: deleteMutation.isLoading,
                  slotLeft: ({ iconProps }) => (
                    <Icon
                      {...iconProps}
                      variant="trash"
                    />
                  ),
                  onClick: () => setIsDeleteConfirmationOpen(true),
                },
              ]}
            >
              <Button
                icon="dots-three-vertical"
                variant="outline"
              />
            </DropdownMenu>
          </IsFeatureAvailable>,
        ]}
        error={assetGroupError}
        onErrorRender={({ error }) => <PageErrorContent error={error} />}
      >
        <Tab
          title="Overview"
          tabKey="details"
        >
          <Titled
            title={generateDocumentTitle(
              breadcrumbs.at(-1)?.title ?? NetworkDetailRoute.title,
              breadcrumbs.at(-2)?.title
            )}
          >
            <NetworkDetailsTab
              viewConfigStorageKey={assetsSharedViewConfigStorageKey}
            />
          </Titled>
        </Tab>
        <Tab
          title={ResourceDefinitions.estimation_method_config.label.plural}
          tabKey="calculations"
        >
          <Titled
            title={generateDocumentTitle(
              `${breadcrumbs.at(-1)?.title ?? localize(Resources.ASSET_GROUP)} ${ResourceDefinitions.estimation_method_config.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            {assetGroup?.default_estimation_method ? (
              <Skeleton isLoading={isLoading}>
                <NetworkCalculationTabV2 />
              </Skeleton>
            ) : (
              <Skeleton isLoading={isLoading}>
                <NetworkCalculationTab />
              </Skeleton>
            )}
          </Titled>
        </Tab>
        <Tab
          title="Results"
          tabKey="results"
        >
          <Titled
            title={generateDocumentTitle(
              `${breadcrumbs.at(-1)?.title ?? localize(Resources.ASSET_GROUP)} ${ResourceDefinitions.calculator_result.label.plural}`,
              breadcrumbs.at(-2)?.title
            )}
          >
            <NetworkResultsTab />
          </Titled>
        </Tab>
        {areFormsEnabled ? (
          <Tab
            tabKey={Resources.FORM_SUBMISSION}
            title="Forms"
          >
            <Titled
              title={generateDocumentTitle(
                `${breadcrumbs.at(-1)?.title ?? localize(Resources.ASSET_GROUP)} ${ResourceDefinitions.form_submission.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedFormSubmissionsTab
                resourceId={networkId}
                resourceType={Resources.ASSET_GROUP}
                defaultFormCategoryName={searchParams.form_category_name}
              />
            </Titled>
          </Tab>
        ) : null}
        {areWorkflowsEnabled ? (
          <Tab
            tabKey={Resources.WORKFLOW}
            title={ResourceDefinitions.workflow.label.plural}
          >
            <Titled
              title={generateDocumentTitle(
                `${breadcrumbs.at(-1)?.title ?? localize(Resources.ASSET_GROUP)} ${ResourceDefinitions.workflow.label.plural}`,
                breadcrumbs.at(-2)?.title
              )}
            >
              <AssociatedWorkflowsTab
                resourceId={networkId}
                resourceType={Resources.ASSET_GROUP}
              />
            </Titled>
          </Tab>
        ) : null}
      </Page>
      <Dialog
        title={`Delete ${toTitleCaseString(localize(Resources.ASSET_GROUP), { intent: "ui" })}`}
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        actionRow={[
          <Button
            key="delete-dialog-action"
            variant="error"
            onClick={() => {
              deleteMutation.mutate({
                id: networkId,
                previousData: assetGroup,
              });
              setIsDeleteConfirmationOpen(false);
            }}
          >
            Delete
          </Button>,
        ]}
      >
        Are you sure you want to delete {assetGroup?.name}? This action cannot
        be undone.
      </Dialog>
      <Dialog
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title={`Delete ${toTitleCaseString(localize(Resources.ASSET_GROUP), { intent: "ui" })}`}
        actionRow={[
          <Button
            key="confirm"
            variant="error"
            onClick={() =>
              deleteMutation.mutate({
                id: networkId,
                previousData: assetGroup,
              })
            }
            isLoading={deleteMutation.isLoading}
            disabled={hasAssociatedResources}
          >
            Delete
          </Button>,
        ]}
      >
        {hasAssociatedResources ? (
          <>
            You cannot delete {assetGroup?.name} until the following associated
            resources are deleted:
            <ul style={{ listStyle: "none", padding: 0, marginBlock: 8 }}>
              {relatedWorkflowsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={NetworkDetailRoute.toLinkParts({
                      pathParams: { networkId },
                      queryParams: { tab: Resources.WORKFLOW },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedWorkflowsQuery.data?.total_entries} Workflows
                  </RoutingLink>
                </li>
              ) : null}
              {relatedFormSubmissionsQuery.data?.total_entries ? (
                <li>
                  <RoutingLink
                    to={NetworkDetailRoute.toLinkParts({
                      pathParams: { networkId },
                      queryParams: { tab: Resources.FORM_SUBMISSION },
                    })}
                    onClickCapture={() => setIsDeleteConfirmationOpen(false)}
                  >
                    {relatedFormSubmissionsQuery.data?.total_entries} Form
                    Submissions
                  </RoutingLink>
                </li>
              ) : null}
            </ul>
            Please delete them and return here to delete this{" "}
            {localize(Resources.ASSET_GROUP)}.
          </>
        ) : (
          <>
            Are you sure you want to delete this{" "}
            {localize(Resources.ASSET_GROUP)}? Calculation run history will be
            deleted. Associated assets such as{" "}
            {localize(`${Resources.FACILITY}_plural`)},{" "}
            {localize(`${Resources.EQUIPMENT}_plural`)}, and{" "}
            {localize(`${Resources.DEVICE}_plural`)} will not be deleted. This
            action cannot be undone.
          </>
        )}
      </Dialog>
      <EditAssetGroupDialog
        assetGroupId={assetIdToEdit}
        onClose={() => setAssetIdToEdit(undefined)}
      />
      <UpdateEntityStatusDialog
        isOpen={isUpdateNetworkStatusDialogOpen}
        onClose={() => {
          setIsUpdateNetworkStatusDialogOpen(false);
        }}
        onSubmit={updateStatusMutation.handleUpdateAssetStatusAsync}
        itemCount={1}
        subject={{
          plural: ResourceDefinitions.asset_group.label.plural,
          singular: ResourceDefinitions.asset_group.label.singular,
        }}
        warningMessage={`This will inactivate all associated equipment, devices, flows, estimation methods in this ${ResourceDefinitions[Resources.ASSET_GROUP].label.singular}. Inactivating estimation methods will delete any associated results for the provided time range.`}
      />
    </>
  );
};
